import { Component, OnInit } from "@angular/core";
import { CommanUsesService } from "src/app/services/comman-uses.service";

import {
  ProductImportCategoryReference,
  ProductImportPartnerReference,
  ProductImportPreviewResponse,
  ProductImportProductReference,
  ProductImportRow,
  ProductImportRowStatus,
} from "../../models/product-import.types";

import { ProductImportService } from "../../services/product-import.service";

@Component({
  selector: "app-product-import",
  templateUrl: "./product-import.component.html",
  styleUrls: ["./product-import.component.scss"],
})
export class ProductImportComponent implements OnInit {
  rows: ProductImportRow[] = [];
  filteredRows: ProductImportRow[] = [];

  activeTab: "all" | ProductImportRowStatus = "all";
  searchText = "";

  selectedFileName = "";

  isUploading = false;
  isValidating = false;
  isImporting = false;
  isDragging = false;

  summary = {
    total: 0,
    valid: 0,
    invalid: 0,
    warning: 0,
  };

  options = {
    skipDuplicates: true,
    updateExisting: false,
    importValidOnly: true,
  };

  columns = [
    { key: "name", label: "Product Name", required: true, width: 210 },
    { key: "sku", label: "SKU", required: true, width: 170 },
    { key: "partner", label: "Partner", required: false, width: 180 },
    { key: "productCategory", label: "Category", required: true, width: 180 },
    { key: "ggDispMin", label: "Preparation Days", required: true, width: 120 },
    {
      key: "productAdvantageDesc",
      label: "Product Plus",
      required: true,
      width: 230,
    },
    { key: "description", label: "Description", required: true, width: 280 },
    { key: "price", label: "Price", required: true, width: 120 },
    { key: "shopifyPrice", label: "Public Price", required: false, width: 120 },
    { key: "line", label: "Line", required: false, width: 170 },
    { key: "image", label: "Image URL", required: false, width: 230 },
    {
      key: "controlStock",
      label: "Control Stock",
      required: false,
      width: 130,
    },
    {
      key: "productAvailability",
      label: "Inventory",
      required: false,
      width: 120,
    },
    {
      key: "adminApproval",
      label: "Admin Approval",
      required: false,
      width: 130,
    },
    { key: "productStatus", label: "Status", required: false, width: 110 },
    { key: "inStock", label: "In Stock", required: false, width: 110 },
    { key: "notPhysical", label: "Not Physical", required: false, width: 120 },
    { key: "isDeluxyProduct", label: "Deluxy", required: false, width: 110 },
    { key: "isCakeProduct", label: "Cake", required: false, width: 110 },
    { key: "isFlowersProduct", label: "Flowers", required: false, width: 110 },
    {
      key: "isBusinessProduct",
      label: "Business",
      required: false,
      width: 110,
    },
    {
      key: "isDeluxyExperienceProduct",
      label: "Experience",
      required: false,
      width: 120,
    },
    {
      key: "isDeluxyDotComProduct",
      label: "Deluxy.com",
      required: false,
      width: 120,
    },
    {
      key: "productHasVariants",
      label: "Has Variants",
      required: false,
      width: 120,
    },
    {
      key: "productOptionTitle",
      label: "Option Title",
      required: false,
      width: 150,
    },
    { key: "variantName", label: "Variant Name", required: false, width: 170 },
    { key: "variantSku", label: "Variant SKU", required: false, width: 170 },
    {
      key: "variantPrice",
      label: "Variant Price",
      required: false,
      width: 130,
    },
    {
      key: "variantShopifyPrice",
      label: "Variant Public Price",
      required: false,
      width: 150,
    },
    {
      key: "variantGgDispMin",
      label: "Variant Prep Days",
      required: false,
      width: 140,
    },
    {
      key: "variantAvailability",
      label: "Variant Inventory",
      required: false,
      width: 140,
    },
    {
      key: "productPartners",
      label: "Product Partners",
      required: false,
      width: 220,
    },
    {
      key: "superProducts",
      label: "Super Products",
      required: false,
      width: 220,
    },
    {
      key: "productVariantsJson",
      label: "Variants JSON",
      required: false,
      width: 300,
    },
  ];

  categories: ProductImportCategoryReference[] = [];
  partners: ProductImportPartnerReference[] = [];
  referenceProducts: ProductImportProductReference[] = [];

  isLoadingReferences = false;
  showReferences = true;

  selectedReferencePartnerId = '';

  categoryReferenceSearch = '';
  partnerReferenceSearch = '';
  productReferenceSearch = '';

  constructor(
    private productImportService: ProductImportService,
    private commanUsesService: CommanUsesService,
  ) {}

  ngOnInit(): void {
    this.loadReferences();
  }

  async loadReferences(): Promise<void> {
    this.isLoadingReferences = true;

    try {
      const response = await this.productImportService.getReferences(this.selectedReferencePartnerId);

      this.categories = response.categories || [];
      this.partners = response.partners || [];
      this.referenceProducts = response.products || [];
    } catch (error) {
      console.error(error);
      this.commanUsesService.showSnackBar("Failed to load import references.");
    } finally {
      this.isLoadingReferences = false;
    }
  }

  onReferencePartnerChange(): void {
    this.categories = [];
    this.categoryReferenceSearch = '';
    this.loadReferences();
  }

  get filteredReferenceCategories(): ProductImportCategoryReference[] {
    const search = (this.categoryReferenceSearch || '').toLowerCase().trim();

    if (!search) {
      return this.categories;
    }

    return this.categories.filter((category) => {
      return (
        [category.id,
        category.categoryName
      ]
        .join(' ')
        .toLowerCase()
        .indexOf(search) !== -1);
    });
  }

  get filteredReferencePartners(): ProductImportPartnerReference[] {
    const search = (this.partnerReferenceSearch || '').toLowerCase().trim();

    if (!search) {
      return this.partners;
    }

    return this.partners.filter((partner) => {
      return (
        [partner.id,
        partner.businessName]
        .join(" ")
        .toLowerCase()
        .indexOf(search) !== -1);
    });
  }

  get filteredReferenceProducts(): ProductImportProductReference[] {
    const search = (this.productReferenceSearch || "").toLowerCase().trim();

    if (!search) {
      return this.referenceProducts;
    }

    return this.referenceProducts.filter((product) => {
      return (
        [product.id, product.name, product.sku]
        .join(" ")
        .toLowerCase()
        .indexOf(search) !== -1);
    });
  }

  downloadCategoryReferenceCsv(): void {
    const rows = [["id", "categoryName"]];

    this.categories.forEach((category) => {
      rows.push([String(category.id), category.categoryName]);
    });

    this.downloadCsv(rows, "product-category-reference.csv");
  }

  downloadPartnerReferenceCsv(): void {
    const rows = [["id", "businessName"]];

    this.partners.forEach((partner) => {
      rows.push([String(partner.id), partner.businessName]);
    });

    this.downloadCsv(rows, "partner-reference.csv");
  }

  downloadProductReferenceCsv(): void {
    const rows = [["id", "name", "sku"]];

    this.referenceProducts.forEach((product) => {
      rows.push([String(product.id), product.name, product.sku || ""]);
    });

    this.downloadCsv(rows, "product-reference.csv");
  }

  private getProductImportErrorMessage(error: any): string {
    const backendMessage = error?.error?.message;
  
    if (Array.isArray(backendMessage)) {
      return backendMessage.join(", ");
    }
  
    if (typeof backendMessage === "string" && backendMessage.trim()) {
      return backendMessage;
    }
  
    if (error?.status === 0) {
      return "The server connection was interrupted while processing the file. The file may be too large, or the backend may have run out of memory.";
    }
  
    if (error?.status === 400) {
      return "The uploaded file is invalid or contains incorrect data.";
    }
  
    if (error?.status === 413) {
      return "The uploaded file is too large. Please upload a smaller file.";
    }
  
    if (error?.status === 415) {
      return "Unsupported file format. Please upload an Excel or CSV file.";
    }
  
    if (error?.status === 500) {
      return "The server failed while reading or validating the file.";
    }
  
    if (error?.status === 502 || error?.status === 503) {
      return "The backend server is temporarily unavailable or stopped while processing the file.";
    }
  
    if (typeof error?.message === "string" && error.message.trim()) {
      return error.message;
    }
  
    return "Failed to upload and validate the product import file.";
  }

  private downloadCsv(rows: string[][], fileName: string): void {
    const csv = rows
      .map((row) => {
        return row
          .map((cell) => {
            return '"' + String(cell || "").replace(/"/g, '""') + '"';
          })
          .join(",");
      })
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    link.click();

    window.URL.revokeObjectURL(url);
  }

  copyText(value: string | number): void {
    const text = String(value);

    if (navigator && navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.commanUsesService.showSnackBar(`${text} copid`);
        })
        .catch((error) => {
          console.error(error);
          this.commanUsesService.showSnackBar('Copy failed Unable to copy text.');
        });

      return;
    }

    try {
      const textarea = document.createElement('textarea');

      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';

      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      document.execCommand('copy');

      document.body.removeChild(textarea);

      this.commanUsesService.showSnackBar(`${text} copid`);

    } catch (error) {
      console.error(error);

      this.commanUsesService.showSnackBar('Copy failed Unable to copy text.');
    }
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files.length) {
      return;
    }

    const file = input.files[0];

    this.selectedFileName = file.name;
    this.uploadFile(file);

    input.value = "";
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();

    this.isDragging = false;

    if (
      !event.dataTransfer ||
      !event.dataTransfer.files ||
      !event.dataTransfer.files.length
    ) {
      return;
    }

    const file = event.dataTransfer.files[0];

    this.selectedFileName = file.name;
    this.uploadFile(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();

    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();

    this.isDragging = false;
  }

  async uploadFile(file: File): Promise<void> {
    this.isUploading = true;

    try {
      const response = await this.productImportService.preview(file);

      this.applyPreview(response);
    } catch (error) {
      console.error("Product import preview failed:", error);
  
      const message = this.getProductImportErrorMessage(error);
  
      this.commanUsesService.showSnackBar(message);
    } finally {
      this.isUploading = false;
    }
  }

  applyPreview(response: ProductImportPreviewResponse): void {
    this.rows = response.rows || [];

    this.summary = {
      total: response.total || 0,
      valid: response.valid || 0,
      invalid: response.invalid || 0,
      warning: response.warning || 0,
    };
  
    this.page = 1;
    this.applyFilters();
  }

  setTab(tab: "all" | ProductImportRowStatus): void {
    this.activeTab = tab;

    this.applyFilters();
  }

  applyFilters(): void {
    const search = (this.searchText || "").toLowerCase().trim();

    this.filteredRows = this.rows.filter((row) => {
      const tabMatch =
        this.activeTab === 'all' || row.status === this.activeTab; 
      const searchableText = [
        row.name,
        row.sku,
        row.partner,
        row.productCategory,
        row.description,
        row.line,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(' ')
        .toLowerCase();
      const searchMatch = !search || searchableText.indexOf(search) !== -1;
      return tabMatch && searchMatch;
    });
  
    this.page = 1;
  }

  updateCell(row: ProductImportRow, field: string, value: string): void {
    (row as any)[field] = value;
  }

  async revalidateRows(): Promise<void> {
    if (!this.rows.length) {
      this.commanUsesService.showSnackBar("Please upload a file first.");
      return;
    }

    this.isValidating = true;

    try {
      const response = await this.productImportService.validateRows(this.rows);

      this.applyPreview(response);
    } catch (error) {
      console.error("Product import row validation failed:", error);

      this.commanUsesService.showSnackBar("Failed to validate rows.");
    } finally {
      this.isValidating = false;
    }
  }

  async importProducts(): Promise<void> {
    if (!this.rows.length) {
      this.commanUsesService.showSnackBar("Please upload a file first.");
      return;
    }

    this.isImporting = true;

    try {
      const previewResponse = await this.productImportService.validateRows(
        this.rows,
      );

      this.applyPreview(previewResponse);

      if (previewResponse.invalid > 0) {
        this.commanUsesService.showSnackBar(
          "Some rows are still invalid. Please fix them before final import.",
        );
        return;
      }

      const response = await this.productImportService.importProducts({
        rows: previewResponse.rows,
        options: this.options,
      });

      this.commanUsesService.showSnackBar(
        "Import completed.\n" +
          "Created: " +
          response.created +
          "\n" +
          "Updated: " +
          response.updated +
          "\n" +
          "Skipped: " +
          response.skipped +
          "\n" +
          "Failed: " +
          response.failed,
      );
    } catch (error: any) {
      if (error && error.error && error.error.preview) {
        this.applyPreview(error.error.preview);
        this.commanUsesService.showSnackBar("Backend validation failed. Please fix highlighted rows.",);
        return;
      }

      console.error(error);
      this.commanUsesService.showSnackBar("Failed to import products.");
    } finally {
      this.isImporting = false;
    }
  }

  reset(): void {
    this.rows = [];
    this.filteredRows = [];
    this.selectedFileName = '';
    this.searchText = '';
    this.activeTab = 'all';
    this.page = 1;
    this.pageSize = 50;
  
    this.summary = {
      total: 0,
      valid: 0,
      invalid: 0,
      warning: 0,
    };
  }

  downloadTemplate(): void {
    this.productImportService.downloadTemplate();
  }

  getFieldError(row: ProductImportRow, field: string): string {
    if (!row.errors) {
      return "";
    }

    const error = row.errors.find((item) => item.field === field);

    return error ? error.message : "";
  }

  getFieldWarning(row: ProductImportRow, field: string): string {
    if (!row.warnings) {
      return "";
    }

    const warning = row.warnings.find((item) => item.field === field);

    return warning ? warning.message : "";
  }

  hasError(row: ProductImportRow, field: string): boolean {
    return !!this.getFieldError(row, field);
  }

  hasWarning(row: ProductImportRow, field: string): boolean {
    return !!this.getFieldWarning(row, field);
  }

  getStatusLabel(row: ProductImportRow): string {
    if (row.status === "valid") {
      return "Valid";
    }

    if (row.status === "warning") {
      return "Warning";
    }

    return "Invalid";
  }

  getReadyCount(): number {
    return this.summary.valid + this.summary.warning;
  }

  trackByRow(index: number, row: ProductImportRow): number {
    return row.rowNumber || index;
  }

  page = 1;
  pageSize = 50;

  pageSizeOptions = [25, 50, 100];

  get displayedRows(): ProductImportRow[] {
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
  
    return this.filteredRows.slice(startIndex, endIndex);
  }
  
  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredRows.length / this.pageSize),
    );
  }
  
  get firstVisibleRow(): number {
    if (!this.filteredRows.length) {
      return 0;
    }
  
    return (this.page - 1) * this.pageSize + 1;
  }
  
  get lastVisibleRow(): number {
    return Math.min(
      this.page * this.pageSize,
      this.filteredRows.length,
    );
  }

  previousPage(): void {
    if (this.page <= 1) {
      return;
    }
  
    this.page--;
  }
  
  nextPage(): void {
    if (this.page >= this.totalPages) {
      return;
    }
  
    this.page++;
  }
  
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
  
    this.page = page;
  }
  
  onPageSizeChange(): void {
    this.page = 1;
  }
}